// FilesPanel — the project folder beside the chat: a tree on top, a preview below.
// Previews by type: images inline; PDF in the browser's own viewer; Markdown and code
// through Rich; CSV parsed into a table; XLSX via SheetJS and DOCX via docx-preview, both
// loaded only when such a file is opened. Everything else offers a download.

import { h, Component, Fragment } from 'preact';
import { Rich, rawUrl } from './rich.js';
import { Icon, fileIcon } from './icons.js';
import { fmtSize } from './transcript.js';

const LANG = { py: 'python', js: 'javascript', mjs: 'javascript', ts: 'typescript', sh: 'bash', json: 'json',
  yml: 'yaml', yaml: 'yaml', toml: 'ini', ini: 'ini', html: 'html', htm: 'html', css: 'css', sql: 'sql',
  rs: 'rust', go: 'go', java: 'java', c: 'c', h: 'c', cpp: 'cpp', xml: 'xml', txt: '', log: '' };
const ext = (p) => (p.split('.').pop() || '').toLowerCase();

function kindOf(path, type) {
  const e = ext(path);
  if ((type || '').startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(e)) return 'image';
  if (e === 'pdf') return 'pdf';
  if (e === 'md' || e === 'markdown') return 'markdown';
  if (e === 'csv' || e === 'tsv') return 'csv';
  if (['xlsx', 'xls', 'ods'].includes(e)) return 'sheet';
  if (e === 'docx') return 'docx';
  if (e === 'html' || e === 'htm') return 'html';
  if (e in LANG || (type || '').startsWith('text/') || type === 'application/json') return 'text';
  return 'binary';
}

// RFC-4180-ish: quotes, doubled quotes, newlines inside quotes.
export function parseCsv(text, sep) {
  sep = sep || (text.split('\n', 1)[0].split('\t').length > text.split('\n', 1)[0].split(',').length ? '\t' : ',');
  const rows = []; let row = [], cell = '', q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"') { if (text[i + 1] === '"') { cell += '"'; i++; } else q = false; } else cell += c;
    } else if (c === '"') q = true;
    else if (c === sep) { row.push(cell); cell = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else cell += c;
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

function Grid({ rows, limit = 500 }) {
  if (!rows.length) return h('p', { className: 'muted' }, 'Empty.');
  const [head, ...body] = rows;
  return h('div', { className: 'md-table-scroll grid' },
    h('table', null,
      h('thead', null, h('tr', null, head.map((c, i) => h('th', { key: i }, c)))),
      h('tbody', null, body.slice(0, limit).map((r, i) => h('tr', { key: i }, r.map((c, j) => h('td', { key: j }, c)))))),
    body.length > limit ? h('p', { className: 'muted' }, `Showing ${limit} of ${body.length} rows.`) : null);
}

class Preview extends Component {
  constructor(p) { super(p); this.state = { text: null, rows: null, sheets: null, sheet: 0, error: null, loading: false }; this.docx = null; }
  componentDidMount() { this.load(); }
  componentDidUpdate(prev) { if (prev.file.path !== this.props.file.path || prev.stamp !== this.props.stamp) this.load(); }

  async load() {
    const { pid, file } = this.props;
    const kind = kindOf(file.path, file.type);
    this.setState({ text: null, rows: null, sheets: null, sheet: 0, error: null, loading: true });
    try {
      if (kind === 'markdown' || kind === 'text' || kind === 'csv') {
        const r = await fetch(`/api/projects/${pid}/file?path=${encodeURIComponent(file.path)}`, { credentials: 'same-origin' });
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || r.statusText);
        if (kind === 'csv') this.setState({ rows: parseCsv(j.content), loading: false, truncated: j.truncated });
        else this.setState({ text: j.content, loading: false, truncated: j.truncated });
      } else if (kind === 'sheet') {
        const [buf, XLSX] = await Promise.all([this.bytes(), import('xlsx')]);
        const wb = XLSX.read(buf, { type: 'array', dense: true });
        const sheets = wb.SheetNames.map((n) => ({ name: n,
          rows: XLSX.utils.sheet_to_json(wb.Sheets[n], { header: 1, raw: false, defval: '' }).map((r) => r.map(String)) }));
        this.setState({ sheets, loading: false });
      } else if (kind === 'docx') {
        const [buf, dp] = await Promise.all([this.bytes(), import('docx-preview')]);
        this.setState({ loading: false });
        if (this.docx) {
          this.docx.innerHTML = '';
          await dp.renderAsync(buf, this.docx, null, { inWrapper: true, ignoreLastRenderedPageBreak: true, experimental: false });
          // a document's links must not become javascript: links on our origin
          this.docx.querySelectorAll('a[href]').forEach((a) => {
            if (!/^(https?:|mailto:|#)/i.test(a.getAttribute('href'))) a.removeAttribute('href');
            a.target = '_blank'; a.rel = 'noopener noreferrer';
          });
        }
      } else this.setState({ loading: false });
    } catch (e) { this.setState({ error: e.message || String(e), loading: false }); }
  }

  async bytes() {
    const r = await fetch(rawUrl(this.props.pid, this.props.file.path), { credentials: 'same-origin' });
    if (!r.ok) { let d = r.statusText; try { d = (await r.json()).detail; } catch (e) { /* */ } throw new Error(d); }
    return r.arrayBuffer();
  }

  render({ pid, file, onOpenFile }, s) {
    const kind = kindOf(file.path, file.type);
    const url = rawUrl(pid, file.path);
    let body;
    if (s.error) body = h('p', { className: 'note err' }, s.error);
    else if (kind === 'image') body = h('div', { className: 'pv-img' }, h('img', { src: url, alt: file.path }));
    else if (kind === 'pdf') body = h('iframe', { className: 'pv-frame', src: url + '#view=FitH', title: file.path });
    else if (kind === 'html') body = h('iframe', { className: 'pv-frame', src: url, sandbox: '', title: file.path });
    else if (s.loading) body = h('div', { className: 'pv-loading' }, h('span', { className: 'spin' }), ' Loading…');
    else if (kind === 'markdown') body = h('div', { className: 'pv-doc' }, h(Rich, { source: s.text || '', pid, onOpenFile }));
    else if (kind === 'text') body = h('div', { className: 'pv-doc' }, h(Rich, { source: '```' + (LANG[ext(file.path)] || '') + '\n' + (s.text || '') + '\n```', pid }));
    else if (kind === 'csv') body = h(Grid, { rows: s.rows || [] });
    else if (kind === 'sheet' && s.sheets) {
      body = h(Fragment, null,
        s.sheets.length > 1 ? h('div', { className: 'tabs' }, s.sheets.map((sh, i) =>
          h('button', { key: i, type: 'button', className: 'tab' + (i === s.sheet ? ' on' : ''), onClick: () => this.setState({ sheet: i }) }, sh.name))) : null,
        h(Grid, { rows: (s.sheets[s.sheet] || {}).rows || [] }));
    } else if (kind === 'docx') body = h('div', { className: 'pv-docx', ref: (el) => { this.docx = el; } });
    else {
      body = h('div', { className: 'pv-none' }, h(Icon, { name: fileIcon(file.path, file.type), size: 40 }),
        h('p', null, 'No preview for this kind of file.'),
        h('a', { className: 'btn', href: rawUrl(pid, file.path, true) }, h(Icon, { name: 'download' }), ' Download'));
    }
    return h('div', { className: 'pv' },
      h('div', { className: 'pv-bar' },
        h(Icon, { name: fileIcon(file.path, file.type) }),
        h('span', { className: 'pv-name', title: file.path }, file.path),
        h('span', { className: 'muted' }, fmtSize(file.size)),
        h('a', { className: 'icon-btn', href: url, target: '_blank', rel: 'noopener', 'aria-label': 'Open in a new tab', title: 'Open in a new tab' }, h(Icon, { name: 'external' })),
        h('a', { className: 'icon-btn', href: rawUrl(pid, file.path, true), 'aria-label': 'Download', title: 'Download' }, h(Icon, { name: 'download' }))),
      s.truncated ? h('p', { className: 'muted pv-trunc' }, 'Showing the first 512 KB. Download for the whole file.') : null,
      h('div', { className: 'pv-body' }, body));
  }
}

export class FilesPanel extends Component {
  constructor(p) { super(p); this.state = { files: null, error: null, open: {} }; }
  componentDidMount() { this.load(); }
  componentDidUpdate(prev) {
    if (prev.pid !== this.props.pid || prev.stamp !== this.props.stamp) this.load();
  }

  async load() {
    if (!this.props.pid) return;
    try {
      const r = await fetch(`/api/projects/${this.props.pid}/files`, { credentials: 'same-origin' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || r.statusText);
      this.setState({ files: j.files, error: null });
    } catch (e) { this.setState({ error: e.message }); }
  }

  render({ pid, selected, onSelect, onClose, stamp }, { files, error, open }) {
    const byDir = {};
    (files || []).forEach((f) => { const d = f.path.includes('/') ? f.path.slice(0, f.path.lastIndexOf('/')) : ''; (byDir[d] ||= []).push(f); });
    const dirs = Object.keys(byDir).sort((a, b) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)));
    const sel = (files || []).find((f) => f.path === selected) || (selected ? { path: selected, size: null } : null);
    return h('aside', { className: 'files-panel', 'aria-label': 'Project files' },
      h('div', { className: 'fp-head' },
        h(Icon, { name: 'folder' }), h('b', null, 'Files'),
        h('span', { className: 'muted' }, files ? `${files.length}` : ''),
        h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Refresh', title: 'Refresh', onClick: () => this.load() }, h(Icon, { name: 'retry' })),
        h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Close files', title: 'Close', onClick: onClose }, h(Icon, { name: 'close' }))),
      h('div', { className: 'fp-tree' + (sel ? ' compact' : '') },
        error ? h('p', { className: 'note err' }, error)
          : !files ? h('div', { className: 'pv-loading' }, h('span', { className: 'spin' }), ' Loading…')
          : !files.length ? h('p', { className: 'muted fp-empty' }, 'Nothing here yet. Files the agent makes, and files you upload, appear here.')
          : dirs.map((d) => h('div', { key: d, className: 'fp-dir' },
            d ? h('button', { type: 'button', className: 'fp-dirname', onClick: () => this.setState({ open: { ...open, [d]: !(open[d] ?? true) } }) },
              h(Icon, { name: 'folder' }), ' ', d) : null,
            (d === '' || (open[d] ?? true)) ? byDir[d].map((f) =>
              h('button', { key: f.path, type: 'button', className: 'fp-file' + (f.path === selected ? ' on' : ''), onClick: () => onSelect(f.path), title: f.path },
                h(Icon, { name: fileIcon(f.path, f.type) }),
                h('span', { className: 'fp-fname' }, d ? f.path.slice(d.length + 1) : f.path),
                h('span', { className: 'muted' }, fmtSize(f.size)))) : null))),
      sel ? h(Preview, { pid, file: sel, stamp, onOpenFile: onSelect }) : null);
  }
}
